/**
 * /workspaces/:wsId/tests — unified Tests page (closes #542).
 *
 * Collapses the prior `/test-plan` + `/test-suites` pages into one
 * screen. Sidebar lists every test in the workspace (search +
 * filters + Group-by: Suite / None / Status / Intent); the right
 * pane swaps content based on what the sidebar selected:
 *
 *   - **Test selected** → side-by-side "What it does" (English plan)
 *     + "How it runs" (Monaco-rendered Playwright spec). Multi-suite
 *     chips on the header for membership editing. View toggle
 *     (Side-by-side / English / Script) + Read/Edit modes.
 *   - **Suite selected** → suite metadata + drag-reorderable member
 *     list + "Add tests" picker + read-only chips for replay
 *     templates that bind this suite.
 *
 * Composition lives at the suite level and uses the same page
 * shell — no third "compose" mode. The pane just shows different
 * content based on selection.
 *
 * **State split:**
 *   - URL-backed (`useTestsSelection`, deep-linkable): selected
 *     entity, group-by dimension, sort.
 *   - localStorage-backed (`usePersistedState` calls inside the
 *     sidebar component): filter dropdowns, selected tags.
 *
 * **Data loading:** centralized in `useTestsData()` — three
 * parallel API calls, indexed for cheap lookup from the sidebar +
 * detail panes. Hand-rolled `useEffect`+`useState` matches the
 * surrounding test-family pages (React Query reserved for the
 * dashboard subtree per convention).
 *
 * **Old route migration:** in the cutover commit, `/test-plan`
 * and `/test-suites` become `<Navigate to="../tests" replace />`
 * and the old page files are deleted. Sidebar collapses two
 * entries into one. Matches the prior route-retirement precedent
 * in App.tsx (no deprecation banner).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { FilePlus2, FolderDown, FolderPlus, Layers } from 'lucide-react'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import { Button, EmptyState, Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui'
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog'
import { DeleteAllIcon } from '@/components/DeleteAllIcon'
import { TestAuthoringTemplateDialog } from '@/components/TestAuthoringTemplateDialog'
import { useManualRunTemplates } from '@/hooks/useManualRunTemplates'
import { useRepoDownload } from './useRepoDownload'
import { useStartRun } from './useStartRun'
import { useReferenceDocExtractionPolling } from '@/hooks/useReferenceDocExtractionPolling'
import { useProposedScriptedStatus } from './useProposedScriptedStatus'
import { useCoverage } from '@/hooks/useCoverage'
import { useToast } from '@/hooks/useToast'
import { ResizablePanels } from '@/components/ResizablePanels'
import { CandidatesSection } from '@/components/CandidatesSection'
import { applicationsAPI } from '@/lib/api'
import { invalidateCoverage } from '@/lib/issueCoverage'
import type { TestSuite } from '@/types'
import { REFERENCE_DOC_ORIGINAL_TOTAL_CAP_BYTES } from '@/components/ReferenceDocsSection'
import { DocumentReviewQueue } from '@/components/DocumentPartsReview'
import { useAppContext } from '@/hooks/useAppContext'
import { testCasesAPI, testSuitesAPI } from '@/lib/api'
import { NewSuiteDialog } from './components/NewSuiteDialog'
import { SuiteDetail } from './components/SuiteDetail'
import { TestDetail } from './components/TestDetail'
import { TestsSidebar } from './components/TestsSidebar'
import { useTestsData } from './useTestsData'
import { useTestsSelection, parseStage, type TestsStage } from './useTestsSelection'
import { ProposedSources, type IssueSourceState } from './ProposedSources'

export default function TestsPage() {
  const {
    selectedApp,
    selectedAppId,
    selectedVariantId,
    selectedVariant,
    setSelectedAppId,
    setSelectedVariantId,
    refreshApp,
  } = useAppContext()
  const wp = useWorkspacePath()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  /** The app a link handed over on mount, until the page is showing it.
   * The document filter is only checked against an app's documents once
   * this is clear, so a link into another app never has its filter
   * checked against the app that was selected before the switch. */
  const pendingHandoffAppId = useRef<string | null>(searchParams.get('appId'))

  // #974 Slice 4 — app + variant handoff URL params. Cross-link
  // sources (today: the Candidates pane's "Open in Tests" button)
  // navigate here with `?appId=<id>&variantId=<id>` to align the
  // global app + variant context with the candidate's scope before
  // the Tests page fetches plans. Both MUST travel together: passing
  // only the variant would silently switch scope to a variant that
  // belongs to a DIFFERENT app than the global header is showing,
  // leaving the user with a mis-routed page (PR #988 Copilot
  // review). On mount we apply both, then strip both — once
  // applied, leaving the params on the URL would let a header-
  // driven app/variant switch later fight a stale handoff.
  useEffect(() => {
    const handoffAppId = searchParams.get('appId')
    const handoffVariantId = searchParams.get('variantId')
    if (!handoffAppId && !handoffVariantId) return
    if (handoffAppId && handoffAppId !== selectedAppId) {
      setSelectedAppId(handoffAppId)
    }
    if (handoffVariantId && handoffVariantId !== selectedVariantId) {
      setSelectedVariantId(handoffVariantId)
    }
    // Strip both — the AppContext's variants-load effect will
    // reconcile selectedVariantId against the new app's variants
    // (validating-or-clobbering) on the next tick. If the variant
    // doesn't belong to the handoff app (broken inbound link, or
    // the variant got deleted between authoring the link and
    // following it), the reconciliation correctly substitutes the
    // app's first variant — the empty-state UX is honest about
    // the failed handoff at that point.
    setSearchParams(
      (prev) => {
        const out = new URLSearchParams(prev)
        out.delete('appId')
        out.delete('variantId')
        return out
      },
      { replace: true },
    )
    // Intentionally limited deps: the handoff is a one-shot mount-
    // time read. Re-running on every selectedAppId / selectedVariantId
    // change would be wrong — the strip would fight the user's
    // subsequent header-driven switches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { cases, suites, scriptByCaseId, loading, error, reload: reloadCases } = useTestsData()
  const { selection, groupBy, sortBy, setSelection, setGroupBy, setSortBy } = useTestsSelection()

  // #2096 — per-reference-doc filter, carried by the View link on a
  // reference-doc row. Unlike the appId / variantId handoff above this
  // is NOT stripped on mount: it is an active filter the user can see
  // and remove, so it stays in the URL where a reload keeps it and the
  // view stays shareable — the same treatment `?stage=` gets.
  const referenceDocId = searchParams.get('referenceDocId')
  // A dashboard gap row narrows further, to one requirement's proposed tests:
  // a document part (`?partId=`, beside its `referenceDocId`) or an
  // imported issue (`?issueConnectionId=&issueExternalId=`). Kept in the
  // URL like the document filter, for the same reasons.
  const partId = searchParams.get('partId')
  const issueConnectionId = searchParams.get('issueConnectionId')
  const issueExternalId = searchParams.get('issueExternalId')
  const issueFilter = useMemo(
    () => (issueConnectionId && issueExternalId
      ? { connectionId: issueConnectionId, externalId: issueExternalId }
      : null),
    [issueConnectionId, issueExternalId],
  )
  // `?sources=open` (the empty coverage tile's link) opens Sources.
  const openSources = searchParams.get('sources') === 'open'
  function clearReferenceDocFilter() {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev)
      out.delete('referenceDocId')
      out.delete('partId')
      return out
    }, { replace: true })
  }
  // A document filter for a document the app no longer has (deleted, or
  // from another app) would hide every proposed test behind a chip that
  // shows only an id. Drop it once the right app's documents are in:
  // a missing list means not loaded yet, not an empty one. It also waits
  // until the handoff has taken `appId` and `variantId` out of the
  // address, because two address updates in one render overwrite each
  // other, and this one would put them back.
  const selectedAppDocs = selectedApp?.id === selectedAppId && Array.isArray(selectedApp.referenceDocs)
    ? selectedApp.referenceDocs
    : null
  const handoffInAddress = searchParams.has('appId') || searchParams.has('variantId')
  if (pendingHandoffAppId.current && selectedApp?.id === pendingHandoffAppId.current) {
    pendingHandoffAppId.current = null
  }
  const filterDocMissing = Boolean(
    referenceDocId
      && !pendingHandoffAppId.current
      && !handoffInAddress
      && selectedAppDocs
      && !selectedAppDocs.some(d => d.id === referenceDocId),
  )
  useEffect(() => {
    if (filterDocMissing) clearReferenceDocFilter()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDocMissing])
  function filterByPart(docId: string, partId: string) {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev)
      out.set('referenceDocId', docId)
      out.set('partId', partId)
      return out
    }, { replace: true })
  }
  function clearIssueFilter() {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev)
      out.delete('issueConnectionId')
      out.delete('issueExternalId')
      return out
    }, { replace: true })
  }

  // #1827 — lifecycle stage, driven by a stable `?stage=` value.
  const stage = parseStage(searchParams.get('stage'))
  function setStage(next: TestsStage) {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev)
      // Default stage keeps the URL clean; a non-default stage is
      // stamped so refresh + bookmark land back on it. Push (the
      // default, not replace) so browser back/forward steps between
      // stages as distinct history entries (#1827 AC6).
      if (next === 'active') out.delete('stage')
      else out.set('stage', next)
      return out
    })
  }

  // #1827 — scope the Proposed stage to exactly the header-selected
  // variant. Handing CandidatesSection a single-variant list keeps its
  // own variant selector hidden (the global header owns variant choice
  // for BOTH stages) while its coverage strip still names the variant.
  const scopedVariants = useMemo(
    () => (selectedVariant ? [{ id: selectedVariant.id, name: selectedVariant.name }] : undefined),
    [selectedVariant],
  )

  const selectedTest = selection?.kind === 'test'
    ? cases.find((c) => c.id === selection.id) ?? null
    : null
  const selectedSuite = selection?.kind === 'suite'
    ? suites.find((s) => s.id === selection.id) ?? null
    : null

  const [confirmingDeleteAll, setConfirmingDeleteAll] = useState(false)
  /** Whether the unified New Suite dialog is open. The dialog
   * (`NewSuiteDialog`) carries all draft state — name, description,
   * Batch/Workflow toggle, members, step labels, skip-rest — and
   * POSTs a single complete payload on Save. Replaces the prior
   * one-click create + duplicate `+ New Workflow` button that
   * shipped in #936 PR 1 (see the dialog's header docstring for
   * the rationale). */
  const [newSuiteOpen, setNewSuiteOpen] = useState(false)
  /** New Test opens the Create Tests settings — the same dialog the Runs
   * page opens — so it starts a run that writes tests rather than creating
   * an empty one here (#2194). */
  const [newTestOpen, setNewTestOpen] = useState(false)
  /** The suite whose name should open ready to edit, set by the suite
   * kebab's Rename (#2194).
   *
   * Cleared as soon as the selection moves, so returning to the same suite
   * later opens it normally. Without that it stays set forever and every
   * later visit steals focus into the name field for someone who never
   * asked to rename anything (Copilot review). */
  const [renamingSuiteId, setRenamingSuiteId] = useState<string | null>(null)
  const [confirmingDeleteSuite, setConfirmingDeleteSuite] = useState<TestSuite | null>(null)

  /** Renaming applies to the selection it was asked for, and only that
   * one. Any later change of selection drops it. */
  useEffect(() => {
    if (renamingSuiteId && !(selection?.kind === 'suite' && selection.id === renamingSuiteId)) {
      setRenamingSuiteId(null)
    }
  }, [selection, renamingSuiteId])
  const { byKind: manualTemplates } = useManualRunTemplates(selectedVariantId)
  const repoDownload = useRepoDownload(selectedAppId ?? '', selectedVariantId ?? undefined)
  const startRun = useStartRun()
  // #2260 — the Proposed tab's per-variant scripted status. Held here,
  // not inside the section, because the page owns the run lifecycle the
  // status has to follow. Restores the fetch that `Applications.tsx`
  // performed before #1827 moved the section here without it.
  const proposedStatus = useProposedScriptedStatus(selectedAppId, selectedVariantId, {
    // The coverage bar must move with the row badges, so every re-read of
    // the status re-reads coverage too.
    onReread: () => { void invalidateCoverage(queryClient) },
    // A finished run can change the suggestions themselves: one it could
    // not write comes back with a reason. Reload the list to show it.
    onRunFinished: () => setProposedRefreshKey(k => k + 1),
  })
  // The Proposed tab's coverage bar reads the one coverage response the
  // dashboard reads. Passed down only when it is for the selected variant.
  const coverageQuery = useCoverage(selectedAppId, selectedVariantId)
  const proposedTestsForVariant =
    coverageQuery.data && coverageQuery.data.variantId === selectedVariantId
      ? coverageQuery.data.proposedTests
      : undefined
  const { refresh: refreshProposedStatus } = proposedStatus
  /** The status snapshot, only when it belongs to the selected variant.
   * The badge, the uncounted filter and the chips all read this, so a
   * variant switch never shows the previous variant's numbers. */
  const statusForVariant =
    proposedStatus.scriptedStatus && proposedStatus.scriptedStatus.variantId === selectedVariantId
      ? proposedStatus.scriptedStatus
      : undefined

  /** Every suggestion of this app, archived ones included, by id: the
   * name and whether it is active. The Suggestion chip on a test reads
   * it. `undefined` while loading or after a failed read, and the chip
   * then stays hidden rather than guessing whether a test is linked. */
  const [suggestionsById, setSuggestionsById] =
    useState<ReadonlyMap<string, { name: string; active: boolean }> | undefined>()
  const [suggestionsKey, setSuggestionsKey] = useState(0)
  useEffect(() => {
    if (!selectedAppId) { setSuggestionsById(undefined); return }
    let cancelled = false
    void applicationsAPI.listCandidates(selectedAppId, { includeInactive: true })
      .then(list => {
        if (!cancelled) setSuggestionsById(new Map(list.map(c => [c._id, { name: c.name, active: c.active }])))
      })
      .catch(() => { if (!cancelled) setSuggestionsById(undefined) })
    return () => { cancelled = true }
  }, [selectedAppId, suggestionsKey])

  // Every change made on the Active tab ends in this reload: deleting a
  // test, deleting them all, saving an edit, adding a test as a
  // suggestion. A deleted test can be the last one linked to a
  // suggestion, so the Proposed tab's statuses are read again too, after
  // the test list, so the read sees the change. Kept as the one `reload`
  // the page hands out, so a new caller cannot forget it.
  const reload = useCallback(async () => {
    await reloadCases()
    refreshProposedStatus()
    setSuggestionsKey(k => k + 1)
  }, [reloadCases, refreshProposedStatus])

  /** "Show them": the Active list narrowed to the tests that count toward
   * no active suggestion. A flag, not a copy of the ids: the ids are read
   * from the current status each render, so the list and the count on the
   * Proposed tab always match, and a test added as a suggestion drops out
   * at once. Cleared on a variant switch, since the ids are per variant. */
  const [showUncounted, setShowUncounted] = useState(false)
  useEffect(() => { setShowUncounted(false) }, [selectedVariantId])
  const uncountedIds = useMemo(
    () => (showUncounted ? new Set(statusForVariant?.uncountedTestIds ?? []) : null),
    [showUncounted, statusForVariant],
  )

  /** A suggestion to open on the Proposed tab, from a test's chip. Rides
   * the URL (`?suggestion=`) with the stage, and is dropped once the
   * section has opened it. */
  const focusSuggestionId = searchParams.get('suggestion')
  function openSuggestion(id: string) {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev)
      out.set('stage', 'proposed')
      out.set('suggestion', id)
      return out
    })
  }
  const clearFocusSuggestion = useCallback(() => {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev)
      out.delete('suggestion')
      return out
    }, { replace: true })
  }, [setSearchParams])

  // #2294 — advance the document statuses in Sources while they are still
  // being read. This is the third thing a move to this page left behind:
  // #1827 dropped the scripted-status fetch (restored above by #2260), and
  // #2158 Slice 2 dropped this loop, which stayed on the Applications page
  // for ten days refreshing a pill that page no longer draws. Hence a
  // tested hook rather than another effect inline in this file.
  //
  // Called before the `selectedApp` guard below, because hooks cannot be
  // conditional; no app simply means nothing to watch.
  useReferenceDocExtractionPolling(
    selectedApp?.referenceDocs,
    () => { void refreshApp(selectedAppId) },
  )

  // Reload the Proposed list whenever a document's read moves on. The
  // polling above refreshes the application, not the list, and a long
  // document saves its tests round by round, so without this the list
  // would only show them on a manual reload. The new tests also change the
  // row badges and the coverage bar, so the status is read again too; that
  // re-read refreshes coverage (see `onReread` above).
  const readSignature = (selectedApp?.referenceDocs ?? [])
    .map(d => `${d.id}:${d.extractionStatus ?? ''}:${d.readProgress?.partsDone ?? ''}:${d.candidateCount ?? ''}`)
    .join('|')
  const [proposedRefreshKey, setProposedRefreshKey] = useState(0)
  const lastReadSignature = useRef(readSignature)
  useEffect(() => {
    if (lastReadSignature.current === readSignature) return
    lastReadSignature.current = readSignature
    setProposedRefreshKey(k => k + 1)
    refreshProposedStatus()
  }, [readSignature, refreshProposedStatus])

  const toast = useToast()

  /** Where this app stands with an issue tracker, for the Import issues
   * button on the Proposed tab's Sources panel.
   *
   * Read from the app's integrations, which also say whether the workspace
   * has any tracker connected. A failed read is its own state, so the panel
   * never tells someone to set up what may already be set up: the request
   * failing, the server saying it couldn't list the trackers, or a response
   * missing either part. Best-effort on purpose: a tracker that is down must
   * not stop the suggestions list from loading, which is the thing this tab
   * is actually for. */
  const [issueSource, setIssueSource] = useState<IssueSourceState>({ kind: 'loading' })
  useEffect(() => {
    setIssueSource({ kind: 'loading' })
    if (!selectedAppId) return
    let cancelled = false
    void applicationsAPI.getIntegrations(selectedAppId)
      .then(res => {
        if (cancelled) return
        // Both parts are read before choosing, so a response missing either
        // throws here and lands in the catch below.
        const projectKeys = res.bindings.issueSource?.projectKeys ?? []
        const { connections, error } = res.issueTracker
        setIssueSource(error || !Array.isArray(connections)
          ? { kind: 'failed' }
          : connections.length === 0
            ? { kind: 'noConnection' }
            : { kind: 'connected', projectKeys })
      })
      .catch(() => { if (!cancelled) setIssueSource({ kind: 'failed' }) })
    return () => { cancelled = true }
  }, [selectedAppId])

  async function deleteAllTests() {
    if (!selectedAppId) return
    await testCasesAPI.deleteAllByApp(selectedAppId)
    setSelection(null)
    setConfirmingDeleteAll(false)
    await reload()
  }

  // Empty state — no app/variant selected. This isn't really a "no
  // tests yet" state; it's an entry-barrier where the operator hasn't
  // picked the scope yet. Keep the title oriented around the action
  // they need to take (pick from the header) rather than implying
  // tests are missing.
  if (!selectedApp || !selectedAppId || !selectedVariantId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Tests</h1>
        <EmptyState
          icon={Layers}
          title="Pick an app and variant"
          body="Tests are scoped to a specific app and variant. Choose both from the header above to view this variant's tests."
        />
      </div>
    )
  }

  // The Proposed badge: suggestions still waiting for a test script in
  // this variant (proposed + planned), from the same snapshot as the bar
  // and the summary line. Not shown until that snapshot is in, rather
  // than falling back to the app-wide total, which is a different number.
  const waitingCount = statusForVariant
    ? statusForVariant.summary.proposed + statusForVariant.summary.planned
    : 0
  const variantLabel = selectedVariant?.name ?? 'this variant'

  // Header counters mirror the prior TestPlan summary line — gives
  // the operator quick scale-of-the-page context.
  const counts = cases.reduce(
    (acc, c) => {
      acc[c.status] = (acc[c.status] ?? 0) + 1
      acc.total++
      return acc
    },
    { total: 0 } as Record<string, number>,
  )
  // The Active badge counts tests only. A sign-in helper sets up a session
  // and is not a test, so it is left out here, as it is from coverage.
  // `counts.total` still includes helpers, because "Delete all" removes
  // them too and must say how many it will remove.
  const testCount = cases.filter(c => c.type !== 'fixture').length

  /** After anything changes a document: refresh the application, and ask
   * for coverage again, since a document's type and its confirmed list
   * decide what counts as a requirement or defect. */
  const onDocumentsChange = () => {
    void refreshApp(selectedAppId)
    void invalidateCoverage(queryClient)
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Tests</h1>
        <p className="text-sm text-muted-foreground">
          The test lifecycle for {selectedApp.name} · {selectedVariant?.name ?? 'this variant'} — from Proposed to Active.
        </p>
      </div>

      {/* Opens the review popup by itself for each document whose read
          has finished but whose list nobody has confirmed. Here, not in
          the documents list, because that list is only mounted while
          "Manage sources" is open. Keyed by app so a put-off document in
          one app does not carry over to the next. */}
      <DocumentReviewQueue
        key={selectedAppId}
        appId={selectedAppId}
        docs={selectedApp.referenceDocs ?? []}
        onChange={onDocumentsChange}
      />

      {/* #1827 — two lifecycle stages. Proposed lists the candidate
          tests the proposer produced (from every source) awaiting
          authoring; Active is today's authored-tests browser. The
          active stage rides a stable `?stage=` value so it survives
          refresh, bookmarking, and back/forward. */}
      <Tabs defaultValue="active" value={stage} onValueChange={(v) => setStage(v as TestsStage)}>
        <TabsList className="w-full justify-start">
          <TabsTrigger value="proposed">
            Proposed
            {waitingCount > 0 && (
              <span
                className="ml-1.5 text-[10px] text-muted-foreground tabular-nums"
                title={waitingCount === 1
                  ? `1 proposed test doesn't have a test script yet in ${variantLabel}`
                  : `${waitingCount} proposed tests don't have a test script yet in ${variantLabel}`}
              >
                {waitingCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="active">
            Active
            {testCount > 0 && (
              <span className="ml-1.5 text-[10px] text-muted-foreground tabular-nums">
                {testCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Proposed = the existing CandidatesSection, relocated from the
            Apps page (#1827). The list, review/multi-select, provenance,
            and app·variant scoping are unchanged — only its home moved. */}
        <TabsContent value="proposed" className="mt-4 space-y-4">
          {/* #2261 — the sources (documents, links and issues) live behind
              the "Manage sources" control in CandidatesSection's toolbar
              instead of stacking above the suggested-tests list. They are
              passed as `sourcesPanel`; the page still owns the app, the
              variant, `refreshApp` and the issue-tracker state.

              The reference-doc "View" link still navigates with
              `?referenceDocId=`, which this page reads and passes down as a
              filter on the list below. Importing issues stays IMPORT-only;
              the link to a tracker is set up on the app's integrations tab. */}
          {/* Keyed by app so nothing from the previous app (its rows, an open
              delete-all dialog and its count) survives a switch in the header.
              Without it, a dialog opened for one app could delete from the next. */}
          <CandidatesSection
            key={selectedAppId}
            refreshKey={proposedRefreshKey}
            appId={selectedAppId}
            appName={selectedApp.name}
            sourcesPanel={
              <ProposedSources
                appId={selectedAppId}
                selectedVariantId={selectedVariantId ?? undefined}
                docs={selectedApp.referenceDocs ?? []}
                totalOriginalCapBytes={REFERENCE_DOC_ORIGINAL_TOTAL_CAP_BYTES}
                onDocumentsChange={onDocumentsChange}
                issueSource={issueSource}
              />
            }
            referenceDocs={selectedApp.referenceDocs}
            referenceDocId={referenceDocId}
            partId={partId}
            onClearReferenceDocFilter={clearReferenceDocFilter}
            onFilterByPart={filterByPart}
            issueFilter={issueFilter}
            onClearIssueFilter={clearIssueFilter}
            openSourcesOnMount={openSources}
            variants={scopedVariants}
            selectedVariantId={selectedVariantId}
            onCandidatesMutated={() => {
              void refreshApp(selectedAppId)
              proposedStatus.refresh()
              setSuggestionsKey(k => k + 1)
            }}
            scriptedStatus={proposedStatus.scriptedStatus}
            proposedTests={proposedTestsForVariant}
            writingCandidateIds={proposedStatus.writingCandidateIds}
            onRunStarted={proposedStatus.onRunStarted}
            onShowUncounted={() => {
              setShowUncounted(true)
              setStage('active')
            }}
            focusCandidateId={focusSuggestionId}
            onFocusCandidateHandled={clearFocusSuggestion}
          />
        </TabsContent>

        {/* Active = today's authored browser, behavior unchanged. */}
        <TabsContent value="active" className="mt-4 space-y-4">
          {/* All-tests actions as icons that name themselves on hover
              (#2265). The counts summary and the kebab are gone — the
              "Active" tab badge already carries the count, and export /
              delete are direct, visible icons (red for the destructive one)
              rather than buried in a menu. Gating and handlers are the ones
              the labelled buttons and kebab items used. */}
          <div className="flex items-center justify-end gap-1.5">
            <Button variant="ghost" size="icon-sm" title="New Suite" aria-label="New Suite"
              onClick={() => setNewSuiteOpen(true)} disabled={counts.total === 0}>
              <FolderPlus className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon-sm" title="New Test" aria-label="New Test"
              onClick={() => setNewTestOpen(true)}
              disabled={!selectedVariantId || !manualTemplates.generation}>
              <FilePlus2 className="h-4 w-4" />
            </Button>
            <span className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />
            <Button variant="ghost" size="icon-sm"
              title={repoDownload.loading ? 'Exporting…' : `Export the tests for ${variantLabel}`} aria-label="Export Tests"
              disabled={repoDownload.loading || !selectedAppId || !selectedVariantId}
              onClick={() => {
                void repoDownload.download().then(result => {
                  if (result.ok) return
                  // 409 is the server saying there is nothing to download
                  // yet; its message says what to do. Anything else is a
                  // real failure and gets the general message.
                  toast({
                    title: 'Could not export',
                    description: result.status === 409
                      ? result.message
                      : `Couldn't download the tests for ${variantLabel}. Try again.`,
                    variant: 'error',
                  })
                })
              }}>
              <FolderDown className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon-sm"
              className="text-destructive hover:text-destructive"
              title="Delete all Tests" aria-label="Delete all Tests"
              disabled={counts.total === 0}
              onClick={() => setConfirmingDeleteAll(true)}>
              <DeleteAllIcon className="h-4 w-4" />
            </Button>
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          {!loading && counts.total === 0 && suites.length === 0 ? (
            <EmptyState
              icon={Layers}
              title="No tests yet"
              body="Tests live alongside the variant they target. Go to Runs and press Create Tests to have OttoTester look around the app and suggest some."
              action={{ label: 'Go to Runs', to: wp('/results') }}
            />
          ) : (
          <div className="h-[calc(100dvh-17rem)] border rounded-lg overflow-hidden">
            <ResizablePanels
              defaultWidth={340}
              minWidth={260}
              maxWidth={520}
              left={
                <TestsSidebar
                  cases={cases}
                  suites={suites}
                  scriptByCaseId={scriptByCaseId}
                  selection={selection}
                  groupBy={groupBy}
                  sortBy={sortBy}
                  onSelect={setSelection}
                  onGroupByChange={setGroupBy}
                  onSortChange={setSortBy}
                  onRenameSuite={(id) => {
                    setSelection({ kind: 'suite', id })
                    setRenamingSuiteId(id)
                  }}
                  onDeleteSuite={setConfirmingDeleteSuite}
                  onlyIds={uncountedIds}
                  onClearOnlyIds={() => setShowUncounted(false)}
                />
              }
              right={
                <div className="h-full bg-card">
                  {/* Right pane swaps based on selection kind:
                      - test → <TestDetail> (English + Monaco)
                      - suite → <SuiteDetail> (members + reorder + chips)
                      - null → "select something" prompt */}
                  {loading ? (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      Loading…
                    </div>
                  ) : !selection ? (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      Select a test or suite from the sidebar
                    </div>
                  ) : selection.kind === 'test' && selectedTest ? (
                    <TestDetail
                      testCase={selectedTest}
                      merged={scriptByCaseId[selectedTest.id]}
                      allCases={cases}
                      allSuites={suites}
                      onReload={reload}
                      onSelectTest={(id) => setSelection({ kind: 'test', id })}
                      suggestionsById={suggestionsById}
                      onOpenSuggestion={openSuggestion}
                    />
                  ) : selection.kind === 'test' && !selectedTest ? (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      Selected test not found — it may have been deleted. Pick another from the sidebar.
                    </div>
                  ) : selection.kind === 'suite' && selectedSuite ? (
                    <SuiteDetail
                      suite={selectedSuite}
                      allCases={cases}
                      scriptByCaseId={scriptByCaseId}
                      onReload={reload}
                      onSelectTest={(id) => setSelection({ kind: 'test', id })}
                      onSuiteDeleted={() => setSelection(null)}
                      autoFocusName={renamingSuiteId === selectedSuite.id}
                      key={`${selectedSuite.id}:${renamingSuiteId === selectedSuite.id ? 'rename' : 'view'}`}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      Selected suite not found — it may have been deleted. Pick another from the sidebar.
                    </div>
                  )}
                </div>
              }
            />
          </div>
          )}
        </TabsContent>
      </Tabs>

      {confirmingDeleteSuite && (
        <ConfirmDeleteDialog
          title={`Delete "${confirmingDeleteSuite.name}"?`}
          /* The suite goes; the tests in it do not. Saying so matters —
             "delete suite" reads as deleting its contents to anyone who
             has not thought about it. */
          message="This removes the suite. The tests in it stay, and keep any other suites they belong to."
          onConfirm={async () => {
            await testSuitesAPI.delete(confirmingDeleteSuite.id)
            if (selection?.kind === 'suite' && selection.id === confirmingDeleteSuite.id) {
              setSelection(null)
            }
            await reload()
          }}
          onCancel={() => setConfirmingDeleteSuite(null)}
        />
      )}

      {confirmingDeleteAll && (
        <ConfirmDeleteDialog
          title={`Delete all ${counts.total} tests?`}
          /* "from here" is the honest qualifier (#2194): the files live in
             a repository, so an engineer can recover them, but nothing in
             the product can — the per-test history needs a test row, and
             after this there are none. */
          message={`This removes every test and every script for "${selectedApp.name}". It cannot be undone from here.`}
          confirmPhrase={selectedApp.name}
          onConfirm={deleteAllTests}
          onCancel={() => setConfirmingDeleteAll(false)}
        />
      )}

      <NewSuiteDialog
        open={newSuiteOpen}
        onOpenChange={setNewSuiteOpen}
        applicationId={selectedAppId}
        variantId={selectedVariantId}
        allCases={cases}
        onCreated={async (created) => {
          await reload()
          // Jump selection to the new suite so the operator lands in
          // detail view with the newly-created suite focused.
          setSelection({ kind: 'suite', id: created.id })
        }}
      />

      {/* New Test opens the Create Tests settings (#2194): the same editor
          the Runs page opens, so there is one place those settings live.
          Saving here starts nothing; the dialog's own Run does. */}
      {newTestOpen && selectedVariantId && manualTemplates.generation && (
        <TestAuthoringTemplateDialog
          template={manualTemplates.generation}
          allTemplates={[]}
          appId={selectedAppId ?? ''}
          variantId={selectedVariantId}
          onClose={() => setNewTestOpen(false)}
          onSaved={() => { setNewTestOpen(false); void reload() }}
          /* Without this the dialog offers Save and Cancel only, so New
             Test would open settings you can edit but never run from —
             which is not what the button says (Codex review). */
          launch={async (settings) => {
            setNewTestOpen(false)
            await startRun.start(
              manualTemplates.generation!.id,
              'Writing tests…',
              settings,
            )
          }}
        />
      )}
    </div>
  )
}
